import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../../api/axios";
import toast from "react-hot-toast";
import { Save, ArrowLeft, Loader2, ImagePlus } from "lucide-react";

export default function ContractedBankManager() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    bankNameTr: "",
    bankNameEn: "",
    bankNameAr: "",
    bankUrl: "",
    bankLogo: null,
    preview: "",
    isActive: true,
    sortOrder: 1,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const settingsRes = await api.get("/setting");
        setSettings(settingsRes.data);

        if (id) {
          const res = await api.get(`/contractedbank/${id}`);
          const d = res.data;
          setFormData({
            bankNameTr: d.bankNameTr || "",
            bankNameEn: d.bankNameEn || "",
            bankNameAr: d.bankNameAr || "",
            bankUrl: d.bankUrl || "",
            bankLogo: null,
            preview: d.bankLogo
              ? `${process.env.REACT_APP_UPLOAD_URL}/${d.bankLogo}`
              : "",
            isActive: d.isActive ?? true,
            sortOrder: d.sortOrder || 1,
          });
        }
      } catch {
        toast.error("Veriler yüklenemedi");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f) {
      setFormData({ ...formData, bankLogo: f });
      const reader = new FileReader();
      reader.onload = (ev) =>
        setFormData((p) => ({ ...p, preview: ev.target.result }));
      reader.readAsDataURL(f);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.bankNameTr.trim() && !formData.bankNameEn.trim()) {
      return toast.error("En az bir dilde banka adı gerekli");
    }
    setSaving(true);

    const data = new FormData();
    data.append("BankNameTr", formData.bankNameTr);
    data.append("BankNameEn", formData.bankNameEn);
    data.append("BankNameAr", formData.bankNameAr);
    data.append("BankUrl", formData.bankUrl);
    data.append("IsActive", formData.isActive);
    data.append("SortOrder", formData.sortOrder);
    if (formData.bankLogo) data.append("file", formData.bankLogo);

    try {
      if (id) {
        await api.put(`/contractedbank/${id}`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Banka güncellendi");
      } else {
        await api.post("/contractedbank", data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Banka eklendi");
      }
      navigate("/contracted-bank-list");
    } catch {
      toast.error(id ? "Banka güncellenemedi" : "Banka eklenemedi");
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="page-loading">
        <div className="loading-spinner" />
        <p>Yükleniyor...</p>
      </div>
    );

  if (!settings && !id)
    return (
      <div className="page-loading">
        <p>Ayarlar bulunamadı.</p>
      </div>
    );

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2>{id ? "Banka Düzenle" : "Banka Ekle"}</h2>
          <p className="page-subtitle">
            {id ? "Mevcut bankayı düzenleyin" : "Yeni anlaşmalı banka ekleyin"}
          </p>
        </div>
        <button
          className="btn btn-ghost"
          onClick={() => navigate("/contracted-bank-list")}
        >
          <ArrowLeft size={18} /> Listeye Dön
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-layout-2col">
          {/* Sol Kolon - İçerik */}
          <div className="form-main">
            {(!settings || settings.turkish) && (
              <div className="content-card">
                <h3 className="card-title">🇹🇷 Türkçe İçerik</h3>
                <div className="form-field">
                  <label>Banka Adı</label>
                  <input
                    type="text"
                    name="bankNameTr"
                    value={formData.bankNameTr}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Türkçe banka adı"
                  />
                </div>
              </div>
            )}

            {(!settings || settings.english) && (
              <div className="content-card">
                <h3 className="card-title">🇬🇧 English Content</h3>
                <div className="form-field">
                  <label>Bank Name</label>
                  <input
                    type="text"
                    name="bankNameEn"
                    value={formData.bankNameEn}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="English bank name"
                  />
                </div>
              </div>
            )}

            {(!settings || settings.arabic) && (
              <div className="content-card">
                <h3 className="card-title">🇸🇦 المحتوى العربي</h3>
                <div className="form-field">
                  <label>اسم البنك</label>
                  <input
                    type="text"
                    name="bankNameAr"
                    value={formData.bankNameAr}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="اسم البنك"
                    dir="rtl"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Sağ Kolon - Ayarlar */}
          <div className="form-sidebar">
            <div className="content-card">
              <h3 className="card-title">Logo</h3>
              <div className="image-upload-area">
                {formData.preview ? (
                  <div className="image-preview logo-preview">
                    <img src={formData.preview} alt="Logo" />
                    <button
                      type="button"
                      className="remove-image"
                      onClick={() =>
                        setFormData((p) => ({
                          ...p,
                          bankLogo: null,
                          preview: "",
                        }))
                      }
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <label className="upload-placeholder">
                    <ImagePlus size={32} />
                    <span>Logo Yükle</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      hidden
                    />
                  </label>
                )}
              </div>
            </div>

            <div className="content-card">
              <h3 className="card-title">Ayarlar</h3>
              <div className="form-field">
                <label>Website URL</label>
                <input
                  type="url"
                  name="bankUrl"
                  value={formData.bankUrl}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="https://example.com"
                />
              </div>

              <div className="form-field">
                <label>Sıralama</label>
                <input
                  type="number"
                  name="sortOrder"
                  value={formData.sortOrder}
                  onChange={handleChange}
                  className="form-input"
                  min="1"
                />
              </div>

              <div className="form-field">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                  />
                  <span>Aktif</span>
                </label>
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-full"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 size={18} className="spin" /> Kaydediliyor...
                </>
              ) : (
                <>
                  <Save size={18} /> {id ? "Güncelle" : "Kaydet"}
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
